import { t, environmentLabel } from "../locale";
import { useEffect, useRef, useState } from "react";
import {
  
  ArrowUpRight,
  Check,

  Ellipsis,
  Info,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  WifiOff,
  X,
} from "lucide-react";
import { config } from "../config";
import { loadDestination, type DestinationConfig } from "./destination";
import { initializeAnalytics, track } from "./analytics";
import { useViewer } from "../members/Viewer";
import { deviceRequest } from "../members/device-session";
import { isHomeScreen } from "./InstallGuide";
import { useInstallAcknowledged } from "./InstallReminder";
import MarketingConsent from "./MarketingConsent";
import "../members/account.css";
import LocalVideoSite from "../videos/VideoSite";

export default function PortalApp() {
  const viewer = useViewer();
  const [saving, setSaving] = useState(false);
  const [destination, setDestination] = useState<DestinationConfig | null>(
    null,
  );
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [frameSlow, setFrameSlow] = useState(false);
  const [dialog, setDialog] = useState<"about" | null>(null);
  const [standalone, setStandalone] = useState(isHomeScreen);
  const installAcknowledged = useInstallAcknowledged();

  const dialogRef = useRef<HTMLDialogElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const openedTarget = useRef("");
  const initialVideoRoute = useRef(location.hash);
  function frameOrigin() {
    try { return new URL(iframeRef.current?.src || "", location.origin).origin; } catch { return ""; }
  }
  function trustedFrame(event: MessageEvent) {
    return event.source === iframeRef.current?.contentWindow && event.origin === frameOrigin() && (event.origin === location.origin || event.origin === config.videoSiteOrigin);
  }

  useEffect(() => {
    const requests = new Set<string>();
    const connect = async (event: MessageEvent) => {
      if (!trustedFrame(event) || event.origin !== config.videoSiteOrigin || event.data?.type !== "yestv:embed-account") return;
      const requestId = event.data.requestId, frame = iframeRef.current?.contentWindow;
      if (typeof requestId !== "string" || !/^[a-zA-Z0-9_-]{8,100}$/.test(requestId) || requests.has(requestId)) return;
      requests.add(requestId);
      if (requests.size > 100) requests.delete(requests.values().next().value!);
      let ticket = "";
      try { ticket = (await deviceRequest<{ ticket: string }>("account/embed-ticket", "POST", { targetOrigin: event.origin })).ticket; } catch { /* The child displays a retry action. */ }
      if (frame && frame === iframeRef.current?.contentWindow) frame.postMessage({ type: "yestv:embed-ticket", requestId, ticket }, event.origin);
    };
    addEventListener("message", connect);
    return () => removeEventListener("message", connect);
  }, [attempt]);

  useEffect(() => {
    const origin = frameOrigin();
    if (origin === config.videoSiteOrigin) iframeRef.current?.contentWindow?.postMessage({ type: "yestv:account-changed" }, origin);
  }, [viewer.user?.userId, viewer.user?.registered, viewer.user?.deviceId]);

  useEffect(() => {
    const onVideoRoute = (event: MessageEvent) => {
      if (!trustedFrame(event) || event.data?.type !== "yestv:video-route") return;
      const hash = event.data.hash;
      if (typeof hash !== "string" || hash.length > 2048 || !/^#(?:home|discover|saved|history|watch\/[0-9a-f-]{36})(?:\?|$)/.test(hash)) return;
      history.replaceState(null, "", `${location.pathname}${location.search}${hash}`);
    };
    const onParentRoute = () => {
      const origin = frameOrigin();
      if (origin === location.origin || origin === config.videoSiteOrigin) iframeRef.current?.contentWindow?.postMessage({ type: "yestv:video-navigate", hash: location.hash || "#home" }, origin);
    };
    addEventListener("message", onVideoRoute);
    addEventListener("hashchange", onParentRoute);
    return () => { removeEventListener("message", onVideoRoute); removeEventListener("hashchange", onParentRoute); };
  }, []);

  useEffect(() => {
    void initializeAnalytics();
    const onDemoEvent = (event: MessageEvent) => {
      if (
        event.origin !== location.origin ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.type !== "yestv:demo"
      )
        return;
      if (["demo_play", "demo_pause", "demo_ended"].includes(event.data.event))
        track(event.data.event);
      if (event.data.event === "demo_ended") viewer.offerRegistration();
    };
    const onAccount = (event: MessageEvent) => {
      if (!trustedFrame(event) || event.data?.type !== "yestv:account" || typeof event.data.requestId !== "string") return;
      if (event.data.action !== "login") return;
      const frame = iframeRef.current?.contentWindow;
      const reason = ["login", "trial", "save", "purchase"].includes(event.data.reason) ? event.data.reason : "login";
      const requestId = event.data.requestId;
      void viewer.requestLogin(reason).then(success => {
        if (frame && frame === iframeRef.current?.contentWindow)
          frame.postMessage({ type: "yestv:account-result", requestId, success }, event.origin);
      });
    };
    addEventListener("message", onDemoEvent);
    addEventListener("message", onAccount);
    const onInstall = (event: MessageEvent) => {
      if (!trustedFrame(event) || event.data?.type !== "yestv:install" || typeof event.data.requestId !== "string") return;
      const frame = iframeRef.current?.contentWindow, requestId = event.data.requestId;
      void viewer.requestInstall(event.data.reason === "manual" ? "manual" : "trial").then(() => {
        if (frame && frame === iframeRef.current?.contentWindow) frame.postMessage({ type: "yestv:install-result", requestId }, event.origin);
      });
    };
    addEventListener("message", onInstall);
    return () => { removeEventListener("message", onDemoEvent); removeEventListener("message", onAccount); removeEventListener("message", onInstall); };
  }, [viewer.user?.registered, viewer.user?.userId]);

  useEffect(() => {
    const target = destination?.destination;
    if (!target || target.demo || target.mode !== "embed") return;
    const origin = new URL(target.url, location.origin).origin;
    const received = new Set<string>();
    const onPlayback = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow || event.data?.type !== "yestv:playback" || !["playing", "preview-ended"].includes(event.data?.event)) return;
      const id = event.data.eventId;
      if (typeof id !== "string" || !/^[a-zA-Z0-9_-]{8,100}$/.test(id) || received.has(id)) return;
      if (received.size >= 1000) return;
      received.add(id);
      if (event.data.event === "preview-ended") { viewer.offerRegistration(); return; }
      track("video_play");
    };
    addEventListener("message", onPlayback);
    return () => removeEventListener("message", onPlayback);
  }, [destination, attempt, viewer.user?.registered]);

  useEffect(() => {
    if (
      destination?.enabled &&
      destination.destination.mode === "embed" &&
      openedTarget.current !== destination.revision
    ) {
      openedTarget.current = destination.revision;
      track("video_open");
    }
  }, [destination]);

  useEffect(() => {
    document.title =
      config.environment === "production"
        ? t("Yestv · Watch")
        : t("Yestv · Playback test");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    setError("");
    setDestination(null);
    setFrameLoaded(false);
    setFrameSlow(false);
    loadDestination(controller.signal, attempt > 0)
      .then((value) => {
        if (active) setDestination(value);
      })
      .catch(() => {
        if (active)
          setError(
            navigator.onLine
              ? t("We couldn’t connect to the video website.")
              : t("Connect to the internet to start watching."),
          );
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt]);

  useEffect(() => {
    if (
      !destination ||
      !destination.enabled ||
      destination.destination.mode !== "embed" ||
      frameLoaded
    )
      return;
    const timeout = setTimeout(() => setFrameSlow(true), 12000);
    return () => clearTimeout(timeout);
  }, [destination, frameLoaded]);

  useEffect(() => {
    const updateMode = () => {
      const homeScreen = isHomeScreen();
      setStandalone(homeScreen);
    };
    const media = matchMedia("(display-mode: standalone)");
    media.addEventListener("change", updateMode);
    addEventListener("pageshow", updateMode);
    addEventListener("focus", updateMode);
    document.addEventListener("visibilitychange", updateMode);
    return () => {
      media.removeEventListener("change", updateMode);
      removeEventListener("pageshow", updateMode);
      removeEventListener("focus", updateMode);
      document.removeEventListener("visibilitychange", updateMode);
    };
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const element = dialogRef.current;
    element?.showModal();
    return () => element?.close();
  }, [dialog]);

  function showInstallGuide() {
    void viewer.requestInstall("manual");
  }

  const target = destination?.destination;
  const blocked =
    error ||
    (destination && !destination.enabled
      ? t("Yestv is taking a short break. Please try again later.")
      : "");

  const localVideo = target && ((new URL(target.url, location.origin).origin === location.origin && new URL(target.url, location.origin).pathname.replace(/\/$/, "") === "/videos") || new URL(target.url, location.origin).origin === config.videoSiteOrigin);
  const inlineVideo = target?.mode === "embed" && new URL(target.url, location.origin).origin === location.origin && new URL(target.url, location.origin).pathname.replace(/\/$/, "") === "/videos";
  const frameUrl = target ? `${target.url}${localVideo ? initialVideoRoute.current : ""}` : "";
  return (
    <div className="portal-page">
      <main className="portal-app">
        <header className="portal-header">
          <a className="wordmark" href="/" aria-label={t("Yestv home")}>
            yes<span>tv</span>
            <i>.</i>
          </a>
          <div className="portal-header-actions">
            {viewer.user?.registered ? <a className="viewer-account-link" href="/account">{viewer.user.nickname}</a> : <button className="viewer-account-link" disabled={!viewer.ready} onClick={() => void viewer.requestLogin("login")}>{!viewer.ready ? "账户连接中…" : !viewer.user ? "重试登录连接" : "注册 / 登录"}</button>}
            {config.environment !== "production" && (
              <span className="test-badge">
                <span /> {t("TEST")}
              </span>
            )}
            <button
              className="round-button"
              aria-label={t("About this preview")}
              onClick={() => setDialog("about")}
            >
              <Ellipsis size={22} />
            </button>
          </div>
        </header>

        <MarketingConsent />

        <section className={`viewing-stage${inlineVideo ? " local-video-stage" : ""}`} aria-label={t("Video website")}>
          {blocked ? (
            <div className="connection-state" role="alert">
              <div className="state-icon">
                <WifiOff size={26} />
              </div>
              <p className="eyebrow">{t("A LITTLE PAUSE")}</p>
              <h1>{t("Let’s reconnect.")}</h1>
              <p>{blocked}</p>
              <button
                className="primary-button"
                onClick={() => setAttempt((value) => value + 1)}
              >
                <RefreshCw size={17} /> {t("Try again")}
              </button>
            </div>
          ) : !destination ? (
            <div className="connection-state" role="status">
              <LoaderCircle className="spin" size={30} />
              <p>{t("Getting your story ready…")}</p>
            </div>
          ) : target?.mode === "navigate" ? (
            <div className="connection-state">
              <div className="state-icon">
                <ArrowUpRight size={28} />
              </div>
              <p className="eyebrow">{t("YOUR NEXT STORY")}</p>
              <h1>{t("Ready when you are.")}</h1>
              <p>
                {t("Continue to")} {destination.name} {t("to start watching.")}
              </p>
              <a
                className="primary-button"
                href={target.url}
                onClick={() => track("video_open")}
              >
                {t("Start watching")} <ArrowUpRight size={18} />
              </a>
            </div>
          ) : inlineVideo && destination ? (
            <LocalVideoSite siteConfig={destination} />
          ) : target ? (
            <>
              {!frameLoaded && (
                <div className="frame-loading" role="status">
                  <LoaderCircle className="spin" size={28} />
                  <span>{t("Opening your story…")}</span>
                </div>
              )}
              <iframe
                key={`${target.url}:${attempt}`}
                ref={iframeRef}
                className="video-site-frame"
                src={frameUrl}
                title={target.demo ? t("Yestv · Playback test") : target.title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => {
                  track("frame_loaded");
                  setFrameLoaded(true);
                  setFrameSlow(false);
                }}
              />
              {frameSlow && (
                <div className="slow-notice">
                  <span>{t("This is taking a little longer.")}</span>
                  <button onClick={() => setAttempt((value) => value + 1)}>
                    {t("Retry")}
                  </button>
                  <a href={target.url} onClick={() => track("video_open")}>
                    {t("Open directly")} <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
            </>
          ) : null}
        </section>

        <footer className="portal-footer">
          <div>
            {!standalone && <button className="viewer-save-button" onClick={showInstallGuide}>{installAcknowledged ? t("Add again") : t("Add to desktop")}</button>}
            {target?.demo && (
              <button
                className="viewer-save-button"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await viewer.changeItem({
                      seriesId: "sintel-preview",
                      title: t("Sintel"),
                      saved: !viewer.items.find(
                        (x) => x.seriesId === "sintel-preview",
                      )?.saved,
                    });
                  } catch {
                    /* Shared sync notice shows the failure. */
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {viewer.items.find((x) => x.seriesId === "sintel-preview")
                  ?.saved
                  ? t("✓ Saved")
                  : t("+ Save story")}
              </button>
            )}
            <span className="footer-status" />
            <span>
              {target?.demo
                ? t("Sample video · Playback preview")
                : t("Your stories, one tap away.")}
            </span>
          </div>
          {standalone && (
            <span className="installed-label">
              <Check size={15} /> {t("On your Home Screen")}
            </span>
          )}
        </footer>
        {viewer.error && (
          <p className="viewer-sync-error" role="status">
            {viewer.error} <a href="/account">{t("My account")}</a>
          </p>
        )}
      </main>

      {dialog && (
        <dialog
          ref={dialogRef}
          className="portal-dialog"
          aria-labelledby="portal-dialog-title"
          onCancel={() => setDialog(null)}
          onClick={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <div className="dialog-top">
            <span className="dialog-grip" />
            <button
              className="round-button"
              onClick={() => setDialog(null)}
              aria-label={t("Close dialog")}
            >
              <X size={20} />
            </button>
          </div>
            <>
              <div className="state-icon">
                <Info size={26} />
              </div>
              <p className="eyebrow">{t("YESTV PREVIEW")}</p>
              <h2 id="portal-dialog-title">{t("Just press play.")}</h2>
              <p>
                {target?.demo
                  ? t(
                      "This preview opens a sample video page so you can try the viewing experience. The existing video website has not been connected yet.",
                    )
                  : localVideo ? "第一集可试看30秒，注册后可完整观看免费集；付费集单独支付解锁，价格以视频页为准。已购集数保存在账户，收藏和观看进度保存在当前浏览器。" : t(
                      "Yestv opens the configured video website. Your video account and purchases are managed by that website.",
                    )}
              </p>
              {target?.demo && (
                <div className="preview-facts">
                  <span>
                    <ShieldCheck size={18} />{" "}
                    {t("No sign-in or payment required")}
                  </span>
                  <span>
                    {t("Sample footage: Sintel, © Blender Foundation")}
                  </span>
                  <a
                    href="https://www.sintel.org/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Sintel · CC BY 3.0")} <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
              <p className="dialog-note">
                Yestv {config.version} · {environmentLabel}
              </p>
              {target && (
                <a
                  className="secondary-button full-width"
                  href={target.url}
                  onClick={() => track("video_open")}
                >
                  {t("Open video page directly")} <ArrowUpRight size={16} />
                </a>
              )}
            </>
        </dialog>
      )}
    </div>
  );
}
